import { Card, Col, Row } from "antd";
import { AxiosError } from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { fetchBookable } from "../../../../apis/This";
import { fetchUnableTime } from "../../../../apis/Unbookable";
import { useWindowResize } from "../../../../hooks/useWIndowResize";
import { alertError } from "../../../../shared/utils";
import { BooksCalendar } from "../Calendar";
import { TimeDataType } from "../Times";
import { DescriptionTypo, TimesTitleTypo } from "../index.styled";
import { TimesTag } from "./Times";

interface UnbookablePreviewProps {
  current?: moment.Moment;
  selectedTimes?: moment.Moment[];
  disableDates?: moment.Moment[];
}

export const UnbookablePreview: React.FC<UnbookablePreviewProps> = ({
  current,
  selectedTimes = [],
  disableDates = [],
}) => {
  const { height } = useWindowResize();

  const [preview, setPreview] = useState<moment.Moment>(); // 미리보기
  const [times, setTimes] = useState<moment.Moment[] | undefined>(
    selectedTimes
  );

  const {
    data: unableTimes,
    isFetched,
    isSuccess,
  } = useQuery(
    ["unableTimes", preview && preview.format("YYYYMMDD")],
    () => fetchUnableTime(preview?.startOf("date").toISOString()),
    {
      enabled: !!preview,
      select: (data) =>
        data
          .filter(({ businessUnabledAt }) =>
            moment(businessUnabledAt).isSame(preview, "date")
          )
          .map(({ businessUnabledAt }) => moment(businessUnabledAt)),
    }
  );

  /**
   *  운영시간 및 예약불가 시간 조회 API
   */
  const { data } = useQuery(
    ["BookableTimes", preview?.format("YYYYMMDD")],
    () => fetchBookable(preview!.startOf("date").toISOString()),
    {
      enabled: !!isFetched,
      select: (data) => {
        const businessTimes = data.businessHourList.map((value) =>
          moment(value)
        );

        const bookableTimes = data.bookableBusinessHourList.map((value) =>
          moment(value)
        );

        return businessTimes.map(
          (time, i) =>
            ({
              key: i + 1,
              time,
              disabled: !bookableTimes.find((bookableTime) =>
                bookableTime.isSame(time)
              ),
            } as TimeDataType)
        );
      },
      onError: (err: AxiosError) => {
        alertError(err.message);
      },
    }
  );

  /**
   * clientheight - Header - tabs - margin(top + bottom) - subMenu - Menu - margin(bottom) - border (bottom; header + tab)
   */
  const layoutHeight = useMemo(
    () => height - 64 - 48 - 24 * 2 - 24 - 48 - 24 - 2,
    [height]
  );

  /**
   * layoutHeight - cardHeader - padding(top + bottom) - description - gap - calendar
   */
  const previewTimeHeight = useMemo(
    () => layoutHeight - 61 - 20 * 2 - 48 - 16,
    [layoutHeight]
  );

  useEffect(() => {
    if (current) {
      setPreview(current);
    }
  }, [current]);

  useEffect(() => {
    setTimes(selectedTimes);
  }, [selectedTimes]);

  useEffect(() => {
    if (isSuccess && !current?.isSame(preview)) {
      setTimes(unableTimes);
    }
  }, [isSuccess]);

  return (
    <Row gutter={[0, 16]} justify="space-between" align="stretch">
      <Col span={24}>
        <DescriptionTypo>
          좌측에 설정한 정보가 잘 반영되었는지 <br />
          캘린더에서 다시 확인해보세요
        </DescriptionTypo>
      </Col>
      <Col>
        <Card bordered={false}>
          <Card.Grid
            style={{ width: "100%", padding: "24px 20px" }}
            hoverable={false}
          >
            <Row gutter={[0, 16]}>
              <Col span={24}>
                <TimesTitleTypo>날짜 선택</TimesTitleTypo>
              </Col>
              <Col span={24}>
                <BooksCalendar
                  current={preview}
                  onChange={(date) => {
                    setPreview(date);
                  }}
                  disableDates={disableDates}
                />
              </Col>
            </Row>
          </Card.Grid>
          <Card.Grid
            style={{
              width: "100%",
              padding: "24px 20px",
              height: previewTimeHeight - 485,
              overflow: "auto",
            }}
            hoverable={false}
          >
            <Row gutter={[0, 16]}>
              <Col span={24}>
                <TimesTitleTypo>시간 선택</TimesTitleTypo>
              </Col>
              <Col span={24}>
                <TimesTag times={data || []} selectedTimes={times} />
              </Col>
            </Row>
          </Card.Grid>
        </Card>
      </Col>
    </Row>
  );
};
